import { useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import { recognizedFood, targets } from "@/mocks/recognition";
import { categoryColors, foodCategories, type FoodNutrition } from "@/mocks/fooddb";
import { parseFoods, findFood, scaleNutrition, nutritionToPer100g } from "@/utils/foodParser";
import { addRecord } from "@/store/recordsStore";

export interface FoodEntry {
  key: string;
  foodId: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  weight: number;
  confidence: number;
  per100g: FoodNutrition;
}

type MealType = "breakfast" | "lunch" | "dinner";

const mealOptions: { key: MealType; label: string; icon: string }[] = [
  { key: "breakfast", label: "早餐", icon: "ri-sun-line" },
  { key: "lunch", label: "午餐", icon: "ri-restaurant-line" },
  { key: "dinner", label: "晚餐", icon: "ri-moon-clear-line" },
];

function currentMealType(): MealType {
  const h = new Date().getHours();
  if (h < 10) return "breakfast";
  if (h < 16) return "lunch";
  return "dinner";
}

function detectMealType(text: string): MealType {
  if (/早餐|早饭|早上/.test(text)) return "breakfast";
  if (/午餐|午饭|中午|中餐/.test(text)) return "lunch";
  if (/晚餐|晚饭|晚上/.test(text)) return "dinner";
  return currentMealType();
}

function currentTime(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function buildInitialEntries(state: { mode?: string; query?: string } | null): FoodEntry[] {
  if (state?.query) {
    return parseFoods(state.query).map((p) => ({
      key: `f-${Math.random().toString(36).slice(2)}-${p.foodId}`,
      foodId: p.foodId,
      name: p.name,
      category: p.category,
      quantity: p.quantity,
      unit: p.unit,
      weight: p.weight,
      confidence: p.confidence,
      per100g: p.per100g,
    }));
  }
  if (state?.mode === "image") {
    const per100g = nutritionToPer100g(recognizedFood.nutrition, recognizedFood.estimatedWeight);
    return [
      {
        key: "img-default",
        foodId: "default",
        name: recognizedFood.name,
        category: "菜",
        quantity: 1,
        unit: "份",
        weight: recognizedFood.estimatedWeight,
        confidence: recognizedFood.confidence,
        per100g,
      },
    ];
  }
  return [];
}

function NutrientBar({ label, value, max, unit, color }: {
  label: string; value: number; max: number; unit: string; color: string;
}) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-400 w-12 whitespace-nowrap">{label}</span>
      <div className="flex-1 h-2 bg-warm-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }}></div>
      </div>
      <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
        {value}<span className="text-gray-400 font-normal">{unit}</span>
      </span>
    </div>
  );
}

export default function RecognizePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as { mode?: string; query?: string } | null) ?? null;

  const [mealType, setMealType] = useState<MealType>(() =>
    state?.query ? detectMealType(state.query) : currentMealType()
  );
  const [entries, setEntries] = useState<FoodEntry[]>(() => buildInitialEntries(state));
  const [editing, setEditing] = useState<FoodEntry | null>(null);
  const [adding, setAdding] = useState(false);
  const [addText, setAddText] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const totalNutrition = useMemo(() => {
    const sum: FoodNutrition = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, vitC: 0 };
    entries.forEach((e) => {
      const n = scaleNutrition(e.per100g, e.weight);
      sum.calories += n.calories;
      sum.protein += n.protein;
      sum.carbs += n.carbs;
      sum.fat += n.fat;
      sum.fiber += n.fiber;
      sum.vitC += n.vitC;
    });
    sum.calories = Math.round(sum.calories);
    sum.protein = Math.round(sum.protein * 10) / 10;
    sum.carbs = Math.round(sum.carbs * 10) / 10;
    sum.fat = Math.round(sum.fat * 10) / 10;
    sum.fiber = Math.round(sum.fiber * 10) / 10;
    sum.vitC = Math.round(sum.vitC * 10) / 10;
    return sum;
  }, [entries]);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 1800);
  };

  const appendFoods = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const parsed = parseFoods(trimmed);
    if (parsed.length === 0) {
      showToast("未能识别，请按「数量+名称」格式输入");
      return;
    }
    setEntries((prev) => [
      ...prev,
      ...parsed.map((p) => ({
        key: `f-${Math.random().toString(36).slice(2)}-${p.foodId}`,
        foodId: p.foodId,
        name: p.name,
        category: p.category,
        quantity: p.quantity,
        unit: p.unit,
        weight: p.weight,
        confidence: p.confidence,
        per100g: p.per100g,
      })),
    ]);
    setAddText("");
    setAdding(false);
  };

  const removeEntry = (key: string) => {
    setEntries((prev) => prev.filter((e) => e.key !== key));
  };

  const saveEdit = (updated: FoodEntry) => {
    setEntries((prev) => prev.map((e) => (e.key === updated.key ? updated : e)));
    setEditing(null);
    showToast("已更新条目");
  };

  const handleSaveAll = () => {
    if (entries.length === 0) {
      showToast("还没有食物，请先添加");
      return;
    }
    setSaving(true);
    setTimeout(() => {
      const time = currentTime();
      entries.forEach((e) => {
        const n = scaleNutrition(e.per100g, e.weight);
        addRecord({
          id: `rec-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          mealType,
          name: e.name,
          category: e.category,
          quantity: e.quantity,
          unit: e.unit,
          weight: e.weight,
          calories: n.calories,
          protein: n.protein,
          carbs: n.carbs,
          fat: n.fat,
          fiber: n.fiber,
          vitC: n.vitC,
          time,
        });
      });
      setSaving(false);
      showToast(`已保存 ${entries.length} 条记录`);
      setTimeout(() => navigate("/records"), 1000);
    }, 700);
  };

  return (
    <AppShell>
      {/* Header */}
      <header className="px-5 pt-5 pb-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white border border-warm-200 flex items-center justify-center cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-gray-600 text-lg"></i>
        </button>
        <h1 className="text-base font-semibold text-gray-900">记录食物</h1>
      </header>

      {/* Meal type selector */}
      <section className="px-5 mt-1">
        <div className="bg-white rounded-card p-1 border border-warm-200 flex gap-1">
          {mealOptions.map((m) => {
            const active = mealType === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setMealType(m.key)}
                className={`flex-1 h-10 rounded-full flex items-center justify-center gap-1.5 text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  active ? "bg-mint-400 text-white font-medium" : "text-gray-500 hover:bg-warm-50"
                }`}
              >
                <i className={m.icon}></i>
                {m.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Food entries */}
      <section className="px-5 mt-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-gray-900">本次食物（{entries.length}）</p>
          <button
            onClick={() => setAdding(!adding)}
            className="text-xs text-mint-500 flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i> 添加食物
          </button>
        </div>

        {adding && (
          <div className="bg-white rounded-card border border-warm-200 p-3 mb-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={addText}
                onChange={(e) => setAddText(e.target.value)}
                placeholder="如：一碗紫菜蛋花汤、一块蛋糕"
                className="flex-1 h-10 rounded-lg bg-warm-50 border border-warm-200 px-3 text-sm text-gray-800 outline-none focus:border-mint-400 placeholder:text-gray-400"
              />
              <button
                onClick={() => appendFoods(addText)}
                className="h-10 px-4 rounded-lg bg-mint-400 text-white text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                添加
              </button>
            </div>
          </div>
        )}

        {entries.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-card border border-warm-200">
            <p className="text-sm text-gray-400">还没有识别到食物</p>
            <p className="text-xs text-gray-400 mt-1">点击「添加食物」输入，或返回首页重新识别</p>
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((e) => {
              const n = scaleNutrition(e.per100g, e.weight);
              return (
                <div key={e.key} className="bg-white rounded-card p-3 border border-warm-200 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${categoryColors[e.category] ?? categoryColors["其他"]}`}>
                        {e.category}
                      </span>
                      <p className="text-sm font-medium text-gray-900 truncate">{e.name}</p>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {e.unit === "克" ? `${e.weight} g` : `${e.quantity}${e.unit} · ${e.weight} g`}
                      {e.confidence < 90 && <span className="text-gray-300"> · 需确认</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">
                      {n.calories}
                      <span className="text-[11px] text-gray-400 font-normal"> kcal</span>
                    </span>
                    <button
                      onClick={() => setEditing(e)}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-mint-500 cursor-pointer transition-colors"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => removeEntry(e.key)}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-red-400 cursor-pointer transition-colors"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Total nutrition */}
      {entries.length > 0 && (
        <section className="px-5 mt-4">
          <div className="bg-white rounded-card p-4 border border-warm-200">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-900">营养合计</p>
              <span className="text-xs text-mint-500 bg-mint-50 px-2 py-1 rounded-md">{totalNutrition.calories} kcal</span>
            </div>
            <div className="space-y-2.5">
              <NutrientBar label="蛋白质" value={totalNutrition.protein} max={targets.protein} unit="g" color="#10B981" />
              <NutrientBar label="碳水" value={totalNutrition.carbs} max={targets.carbs} unit="g" color="#F59E0B" />
              <NutrientBar label="脂肪" value={totalNutrition.fat} max={targets.fat} unit="g" color="#EF4444" />
              <NutrientBar label="纤维" value={totalNutrition.fiber} max={targets.fiber} unit="g" color="#8B5CF6" />
              <NutrientBar label="维C" value={totalNutrition.vitC} max={targets.vitC} unit="mg" color="#F97316" />
            </div>
          </div>
        </section>
      )}

      {/* Actions */}
      <section className="px-5 mt-4 pb-6">
        <button
          onClick={handleSaveAll}
          className="w-full h-12 rounded-btn bg-mint-400 text-white text-sm font-medium flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin text-lg"></i> 保存中...
            </>
          ) : (
            <>
              <i className="ri-check-line text-lg"></i> 保存全部到记录
            </>
          )}
        </button>
        <button
          onClick={() => navigate("/")}
          className="w-full h-12 mt-2 rounded-btn bg-white border border-warm-200 text-gray-600 text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          重新识别
        </button>
      </section>

      {/* Edit modal */}
      {editing && (
        <EditModal entry={editing} onClose={() => setEditing(null)} onSave={saveEdit} />
      )}

      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-gray-900/85 text-white text-xs px-4 py-2 rounded-full z-50 whitespace-nowrap">
          {toast}
        </div>
      )}
    </AppShell>
  );
}

function EditModal({ entry, onClose, onSave }: {
  entry: FoodEntry; onClose: () => void; onSave: (e: FoodEntry) => void;
}) {
  const [name, setName] = useState(entry.name);
  const [category, setCategory] = useState(entry.category);
  const [quantity, setQuantity] = useState(entry.quantity);
  const [unit, setUnit] = useState(entry.unit);
  const [weight, setWeight] = useState(entry.weight);
  const [per100g, setPer100g] = useState<FoodNutrition>(entry.per100g);
  const [foodId, setFoodId] = useState(entry.foodId);

  const preview = scaleNutrition(per100g, weight);

  const handleName = (v: string) => {
    setName(v);
    const food = findFood(v);
    if (food && food.id !== foodId) {
      setFoodId(food.id);
      setCategory(food.category);
      setPer100g(food.per100g);
      setUnit(food.defaultUnit);
      setWeight(Math.max(1, Math.round(food.unitWeight * quantity)));
    }
  };

  const handleQuantity = (v: number) => {
    const q = Math.max(0, v);
    setQuantity(q);
    const food = findFood(name);
    if (food) setWeight(Math.max(1, Math.round(food.unitWeight * q)));
  };

  const save = () => {
    if (weight <= 0) return;
    onSave({
      key: entry.key,
      foodId,
      name: name.trim() || entry.name,
      category,
      quantity,
      unit,
      weight,
      confidence: entry.confidence,
      per100g,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative w-full max-w-md bg-white rounded-t-2xl p-5 pb-8">
        <div className="flex items-center justify-between mb-4">
          <p className="text-base font-semibold text-gray-900">编辑条目</p>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-warm-50 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-400 block mb-1">食物名称</label>
            <input
              type="text"
              value={name}
              onChange={(e) => handleName(e.target.value)}
              className="w-full h-11 rounded-lg bg-warm-50 border border-warm-200 px-3 text-sm text-gray-800 outline-none focus:border-mint-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 block mb-1">类别</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full h-11 rounded-lg bg-warm-50 border border-warm-200 px-3 text-sm text-gray-800 outline-none focus:border-mint-400 cursor-pointer"
              >
                {foodCategories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">数量</label>
              <div className="flex items-center gap-1 h-11 rounded-lg bg-warm-50 border border-warm-200 px-3">
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={quantity}
                  onChange={(e) => handleQuantity(Number(e.target.value))}
                  className="w-full text-sm text-gray-800 outline-none bg-transparent text-right"
                />
                <span className="text-xs text-gray-400">{unit}</span>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 block mb-1">重量（克）</label>
            <div className="flex items-center gap-1 h-11 rounded-lg bg-warm-50 border border-warm-200 px-3">
              <input
                type="number"
                min="0"
                value={weight}
                onChange={(e) => setWeight(Number(e.target.value))}
                className="w-full text-sm text-gray-800 outline-none bg-transparent text-right"
              />
              <span className="text-xs text-gray-400">g</span>
            </div>
          </div>

          <div className="rounded-lg bg-mint-50 p-3 grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-xs text-gray-400">热量</p>
              <p className="text-sm font-semibold text-gray-800">{preview.calories} kcal</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">蛋白质</p>
              <p className="text-sm font-semibold text-gray-800">{preview.protein} g</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">碳水</p>
              <p className="text-sm font-semibold text-gray-800">{preview.carbs} g</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">脂肪</p>
              <p className="text-sm font-semibold text-gray-800">{preview.fat} g</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">纤维</p>
              <p className="text-sm font-semibold text-gray-800">{preview.fiber} g</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">维C</p>
              <p className="text-sm font-semibold text-gray-800">{preview.vitC} mg</p>
            </div>
          </div>
        </div>

        <button
          onClick={save}
          className="w-full h-12 mt-5 rounded-btn bg-mint-400 text-white text-sm font-medium flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-check-line text-lg"></i> 保存
        </button>
      </div>
    </div>
  );
}